import './dashboardTheme.css';
import React, { useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  CircleDashed,
  RefreshCw,
  Activity,
} from 'lucide-react';
import DashboardPanel from './DashboardPanel';
import { useLanguage } from '../../contexts/LanguageContext';
import { BACKEND_URL } from '../../utils/backendURL';
import useQualityOverview from './useQualityOverview';
import HistoricalAnalytics from './HistoricalAnalytics';
import { qualityCopy, projectName } from './qualityOverviewCopy';
import {
  FailureBreakdown,
  ProjectHealth,
  RecentActivity,
  formatDate,
} from './QualityOverviewPanels';

export default function DashboardOverview({ navigateToTestHistory }) {
  const { language } = useLanguage();
  const copy = qualityCopy(language);
  const [tab, setTab] = useState('overview');
  const view = useQualityOverview(tab === 'analytics');
  const [opening, setOpening] = useState('');
  const [openError, setOpenError] = useState(false);
  const { data, loading, error } = view;
  const summary = data?.summary;
  const reset = () => {
    view.setProject('');
    view.setType('all');
    view.setDays(7);
  };

  const openHistory = async (test) => {
    setOpening(`${test.type}-${test.id}`);
    setOpenError(false);
    try {
      const surface = test.type === 'flow_test' ? 'flow-testing' : 'api-testing';
      const response = await axios.get(
        `${BACKEND_URL}/api/${surface}/tests/${encodeURIComponent(test.id)}`,
      );
      navigateToTestHistory({ ...response.data, type: test.type });
    } catch (_) {
      setOpenError(true);
    } finally {
      setOpening('');
    }
  };

  return (
    <section className="lexa-dashboard mx-auto max-w-7xl space-y-6" aria-label={copy.title}>
      <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950">{copy.title}</h2>
            {summary && (
              <span
                className={`text-xs font-medium px-2.5 py-1 rounded-full ${summary.currently_failing || summary.never_run || summary.stale ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-600'}`}
              >
                {summary.currently_failing || summary.never_run || summary.stale
                  ? copy.attentionNeeded
                  : summary.completed
                    ? copy.resultsAvailable
                    : copy.noRecentData}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-2">{copy.subtitle}</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          {data && (
            <span>
              {summary.total_tests} {copy.visibleTests} · {copy.updated}{' '}
              {formatDate(data.to, language)}
            </span>
          )}
          <button
            type="button"
            onClick={view.refresh}
            disabled={loading}
            className="border rounded-lg px-3 py-2 bg-white hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2 text-slate-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {copy.refresh}
          </button>
        </div>
      </div>

      <div role="tablist" aria-label="Dashboard views" className="flex w-fit max-w-full gap-1 rounded-xl border border-slate-200 bg-white p-1">
        {['overview', 'analytics'].map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === value ? 'bg-[#6d28d9] text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            {value === 'overview'
              ? language === 'es'
                ? 'Resumen'
                : 'Overview'
              : language === 'es'
                ? 'Analítica'
                : 'Analytics'}
          </button>
        ))}
      </div>
      <DashboardPanel className="px-5 py-4">
        <div className="flex flex-wrap gap-4 items-end">
          <label className="flex-1 min-w-[180px] text-xs font-medium text-slate-600">
            {copy.project}
            <select
              aria-label={copy.project}
              className="block w-full mt-1.5 border border-slate-200 rounded-lg px-3 py-2 bg-white text-sm text-slate-900"
              value={view.project}
              onChange={(event) => view.setProject(event.target.value)}
            >
              <option value="">{copy.allProjects}</option>
              {view.projects.map((project) => (
                <option value={project.id} key={project.id}>
                  {projectName(project, copy)}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-[150px] text-xs font-medium text-slate-600">
            {copy.type}
            <select
              aria-label={copy.type}
              className="block w-full mt-1.5 border border-slate-200 rounded-lg px-3 py-2 bg-white text-sm text-slate-900"
              value={view.type}
              onChange={(event) => view.setType(event.target.value)}
            >
              <option value="all">{copy.allTypes}</option>
              <option value="api_test">{copy.api}</option>
              <option value="flow_test">{copy.flow}</option>
            </select>
          </label>
          <label className="min-w-[120px] text-xs font-medium text-slate-600">
            {copy.period}
            <select
              aria-label={copy.period}
              className="block w-full mt-1.5 border border-slate-200 rounded-lg px-3 py-2 bg-white text-sm text-slate-900"
              value={view.days}
              onChange={(event) => view.setDays(Number(event.target.value))}
            >
              {[7, 30, 90].map((days) => (
                <option value={days} key={days}>
                  {days} {copy.days}
                </option>
              ))}
            </select>
          </label>
          {(view.project || view.type !== 'all' || view.days !== 7) && (
            <button
              type="button"
              onClick={reset}
              className="text-sm text-violet-700 py-2 hover:underline"
            >
              {copy.reset}
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-3 leading-relaxed">{copy.filtersHint}</p>
      </DashboardPanel>

      {loading && (
        <div
          role="status"
          className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500"
        >
          {copy.loading}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-800"
        >
          <p>{copy.error}</p>
          <button type="button" onClick={view.refresh} className="mt-3 underline font-medium">
            {copy.retry}
          </button>
        </div>
      )}
      {openError && (
        <p role="alert" className="text-sm text-rose-700">
          {copy.openError}
        </p>
      )}
      {data && tab === 'analytics' && data.history && (
        <HistoricalAnalytics
          data={data}
          language={language}
          onProject={view.setProject}
          onOpen={openHistory}
          opening={opening}
        />
      )}
      {data && tab === 'overview' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {[
              {
                title: copy.completed,
                value: summary.completed,
                hint: `${summary.passed} ${copy.passed} · ${summary.failed} ${copy.failed}`,
                icon: Activity,
                color: 'text-violet-600 bg-violet-50',
                note: copy.completedHint,
              },
              {
                title: copy.successRate,
                value: summary.success_rate === null ? '—' : `${summary.success_rate}%`,
                hint: summary.completed ? copy.successHint : copy.noCompleted,
                icon: CheckCircle2,
                color: 'text-emerald-600 bg-emerald-50',
              },
              {
                title: copy.failing,
                value: summary.currently_failing,
                hint: copy.failingHint,
                icon: AlertTriangle,
                color: 'text-rose-600 bg-rose-50',
              },
              {
                title: copy.neverRun,
                value: summary.never_run,
                hint: copy.neverHint,
                icon: CircleDashed,
                color: 'text-slate-600 bg-slate-100',
              },
            ].map(({ title, value, hint, icon: Icon, color, note }) => (
              <DashboardPanel key={title} className="p-5">
                <div className="flex justify-between items-start gap-2">
                  <h3 className="text-sm font-medium text-slate-600">{title}</h3>
                  <span className={`rounded-lg p-2 ${color}`}>
                    <Icon className="w-4 h-4" />
                  </span>
                </div>
                <p className="text-3xl font-semibold tracking-tight tabular-nums mt-3 text-slate-900">
                  {value}
                </p>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed">{hint}</p>
                {note && <p className="text-xs text-slate-400 mt-1">{note}</p>}
              </DashboardPanel>
            ))}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <DashboardPanel className="p-6">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-slate-900">{copy.attention}</h3>
                <span className="text-xs rounded-full bg-slate-100 text-slate-600 px-2.5 py-1">
                  {data.attention_total}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1 mb-4">{copy.attentionHint}</p>
              {!data.attention.length && (
                <p className="text-sm text-slate-500 py-8">{copy.noAttention}</p>
              )}
              <div className="divide-y divide-slate-100">
                {data.attention.map((test) => (
                  <button
                    type="button"
                    key={`${test.type}-${test.id}`}
                    disabled={Boolean(opening)}
                    onClick={() => openHistory(test)}
                    aria-label={`${copy.history}: ${test.name}`}
                    className="w-full py-3.5 flex justify-between items-center gap-3 text-left group disabled:opacity-50"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate group-hover:text-violet-700">
                        {test.name}
                      </p>
                      <p
                        className={`text-xs mt-1 ${test.reason.includes('fail') ? 'text-rose-700' : 'text-slate-500'}`}
                      >
                        {copy[test.reason]}
                        {test.reason === 'repeated_failure' ? ` · ${test.failed_executions}` : ''}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {test.type === 'api_test' ? copy.api : copy.flow} · {copy.lastRun}:{' '}
                        {formatDate(test.last_run_at, language)}
                      </p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                ))}
              </div>
              {data.attention_total > data.attention.length && (
                <p className="text-xs text-slate-400 mt-3">
                  {copy.showing} {data.attention.length} {copy.of} {data.attention_total}
                </p>
              )}
            </DashboardPanel>
            <FailureBreakdown data={data} copy={copy} onProject={view.setProject} />
          </div>
          <ProjectHealth data={data} copy={copy} language={language} onProject={view.setProject} />
          <RecentActivity
            data={data}
            copy={copy}
            language={language}
            onOpen={openHistory}
            opening={opening}
          />
        </>
      )}
    </section>
  );
}
